import React, {useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';

import logoMbank from '../assets/logo-mbank.svg';
import logoObank from '../assets/logo-obank.svg';
import logoElqr from '../assets/logo-elqr.svg';
import logoMegapay from '../assets/logo-megapay.svg';

export type Gateway = 'finik';
export type PaymentMethod = 'qr' | 'card';

type Props = {
    open: boolean;
    amount: number;
    loading?: boolean;
    onClose: () => void;
    onConfirm: (gateway: Gateway, method: PaymentMethod) => void;
};

const PaymentMethodModal: React.FC<Props> = ({open, amount, loading, onClose, onConfirm}) => {
    const {t} = useTranslation();
    const backdropRef = useRef<HTMLDivElement>(null);
    const [selectedMethod, setSelectedMethod] = React.useState<'qr' | 'card' | null>(null);

    // Блокируем скролл фона
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    // Сбрасываем выбор при открытии модального окна
    useEffect(() => {
        if (open) {
            setSelectedMethod(null);
        }
    }, [open]);

    // ESC для закрытия
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const onBackdropClick = (e: React.MouseEvent) => {
        if (e.target === backdropRef.current) onClose();
    };

    if (!open) return null;

    const modal = (
        <div
            ref={backdropRef}
            onClick={onBackdropClick}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
            aria-modal="true"
            role="dialog"
        >
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="bg-white rounded-[20px] shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="px-6 pt-6">
                        <h3 className="text-[22px] font-semibold text-accent">{t('payment_modal.title')}</h3>
                        <p className="text-sm text-gray-600 mt-1">{t('payment_modal.subtitle')}</p>
                    </div>

                    {/* Payment Methods */}
                    <div className="px-6 py-5 space-y-3">
                        {/* Finik QR */}
                        <button
                            onClick={() => setSelectedMethod('qr')}
                            disabled={loading}
                            className={`w-full text-left p-4 rounded-[16px] border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                selectedMethod === 'qr'
                                    ? 'border-[#93E532] bg-[#F4FFE7]'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                    selectedMethod === 'qr' ? 'border-[#93E532]' : 'border-gray-300'
                                }`}>
                                    {selectedMethod === 'qr' && (
                                        <div className="w-3 h-3 rounded-full bg-[#93E532]"></div>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="font-medium">{t('payment_modal.finik_title')}</div>
                                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                                        <img src={logoMbank} className="h-4" alt="MBank"/>
                                        <img src={logoElqr} className="h-5" alt="ElQR"/>
                                        <img src={logoMegapay} className="h-4" alt="MegaPay"/>
                                        <img src={logoObank} className="h-5" alt="O!Bank"/>
                                    </div>
                                </div>
                            </div>
                        </button>

                        {/* Visa/MasterCard */}
                        <button
                            onClick={() => setSelectedMethod('card')}
                            disabled={loading}
                            className={`w-full text-left p-4 rounded-[16px] border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                selectedMethod === 'card'
                                    ? 'border-[#93E532] bg-[#F4FFE7]'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                    selectedMethod === 'card' ? 'border-[#93E532]' : 'border-gray-300'
                                }`}>
                                    {selectedMethod === 'card' && (
                                        <div className="w-3 h-3 rounded-full bg-[#93E532]"></div>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="font-medium">{t('payment_modal.card_title')}</div>
                                    <div className="mt-2 flex items-center gap-3">
                                        <svg width="35" height="35" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" shapeRendering="geometricPrecision">
                                            <g clipPath="url(#clip0_visa_35)">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M10.0004 26.8988H6.53867L3.94278 16.8137C3.81957 16.3498 3.55795 15.9397 3.17313 15.7464C2.21275 15.2606 1.15448 14.8741 0 14.6791V14.2908H5.5766C6.34626 14.2908 6.9235 14.8741 7.0197 15.5514L8.36659 22.8261L11.8267 14.2908H15.1922L10.0004 26.8988ZM17.1163 26.8988H13.847L16.5391 14.2908H19.8084L17.1163 26.8988ZM24.0381 17.7836C24.1343 17.1045 24.7116 16.7162 25.385 16.7162C26.4433 16.6187 27.5961 16.8137 28.5582 17.2978L29.1354 14.5833C28.1733 14.195 27.1151 14 26.1547 14C22.9816 14 20.6726 15.7464 20.6726 18.1702C20.6726 20.014 22.3081 20.9822 23.4626 21.5655C24.7116 22.147 25.1926 22.5353 25.0964 23.1169C25.0964 23.9892 24.1343 24.3775 23.174 24.3775C22.0195 24.3775 20.865 24.0867 19.8084 23.6009L19.2312 26.3172C20.3857 26.8013 21.6347 26.9962 22.7891 26.9962C26.3471 27.092 28.5582 25.3473 28.5582 22.7286C28.5582 19.4308 24.0381 19.2375 24.0381 17.7836ZM40 26.8988L37.4041 14.2908H34.6158C34.0386 14.2908 33.4613 14.6791 33.2689 15.2606L28.462 26.8988H31.8275L32.4993 25.0566H36.6345L37.0193 26.8988H40ZM35.0968 17.6861L36.0572 22.4379H33.3651L35.0968 17.6861Z" fill="#172B85"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_visa_35">
                                                    <rect width="40" height="40" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <svg width="30" height="30" viewBox="0 5 40 25" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" shapeRendering="geometricPrecision">
                                            <path d="M25.4083 27.0649H14.5923V7.64087H25.4083V27.0649Z" fill="#FF5F00"/>
                                            <path d="M15.2785 17.3533C15.2785 13.4131 17.1247 9.90322 19.9996 7.64133C17.8972 5.98732 15.2439 5.0001 12.3603 5.0001C5.53374 5.0001 0 10.5307 0 17.3533C0 24.1759 5.53374 29.7065 12.3603 29.7065C15.2439 29.7065 17.8972 28.7193 19.9996 27.0653C17.1247 24.8034 15.2785 21.2936 15.2785 17.3533Z" fill="#EB001B"/>
                                            <path d="M40 17.3533C40 24.1759 34.4662 29.7065 27.6396 29.7065C24.756 29.7065 22.1027 28.7193 19.9995 27.0653C22.8752 24.8034 24.7214 21.2936 24.7214 17.3533C24.7214 13.4131 22.8752 9.90322 19.9995 7.64133C22.1027 5.98732 24.756 5.0001 27.6396 5.0001C34.4662 5.0001 40 10.5307 40 17.3533Z" fill="#F79E1B"/>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </button>
                    </div>

                    {/* Footer */}
                    <div className="px-6 pb-6">
                        <div className="flex items-center justify-between">
                            <button
                                onClick={onClose}
                                className="px-6 py-3 rounded-xl border border-gray-300 hover:bg-gray-50 transition-colors"
                            >
                                {t('payment_modal.cancel')}
                            </button>

                            <button
                                disabled={loading || !selectedMethod}
                                onClick={() => selectedMethod && onConfirm('finik', selectedMethod)}
                                className={`py-4 px-6 rounded-full font-serif font-normal text-white transition inline-flex items-center justify-center gap-2 ${
                                    loading || !selectedMethod
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-gradient-to-br from-[#93E532] to-accent shadow-[0_8px_16px_rgba(147,229,50,0.35)] hover:opacity-90'
                                }`}
                            >
                                <span className="sm:hidden">
                                    {loading ? t('payment_modal.loading') : t('payment_modal.donate_button')}
                                </span>
                                <span className="hidden sm:inline">
                                    {loading ? t('payment_modal.loading') : t('payment_modal.donate_amount', {amount: amount.toLocaleString('ru-RU')})}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
};

export default PaymentMethodModal;